//----------- IMPORTAÇÕES -----------//
import { useEffect, useState, useRef, useContext } from "react";
import { Flame, Search, X, Loader2, ChevronLeft, ChevronRight } from "lucide-react";
import { SidebarContext } from "../../shared/context/SidebarContext";
import { loadLeadsQuentesService, loadTagsService } from "./services";
import ContainerLeadsQuentes from "./components/ContainerLeadsQuentes";


//----------- CONSTANTES -----------//
const ITEMS_PER_PAGE = 12;
const PERIODOS = [
    { valor: 30, label: "30 dias" },
    { valor: 60, label: "60 dias" },
    { valor: 90, label: "90 dias" },
];

// Termos que disparam pré-seleção automática (case/acento-insensitive) na primeira carga.
const TERMOS_EXCLUSAO_PADRAO = [
    "nao tenho interesse",
    "sem interesse",
    "cancelar",
    "cancelado",
    "nao perturbe",
    "desinteresse",
];


//----------- HELPER: NORMALIZA (remove acento + lowercase) -----------//
function normalizar(texto) {
    return (texto || "")
        .toString()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase();
}


//----------- EXPORTAÇÃO DA PÁGINA LEADS QUENTES -----------//
export default function LeadsQuentes() {

    //----------- HOOKS DE LAYOUT -----------//
    const { isOpen } = useContext(SidebarContext);

    //----------- ESTADO -----------//
    const [leads, setLeads] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [erro, setErro] = useState(false);

    const [currentPage, setCurrentPage] = useState(1);
    const [busca, setBusca] = useState("");
    const [buscaDebounced, setBuscaDebounced] = useState("");
    const [periodo, setPeriodo] = useState(30);
    const [comprou, setComprou] = useState("todos"); // "todos" | "sim" | "nao"

    const [tagsAvailable, setTagsAvailable] = useState([]);
    const [excluirTags, setExcluirTags] = useState([]); // array de ids
    const preSelecionou = useRef(false);

    //----------- DEBOUNCE DA BUSCA -----------//
    useEffect(() => {
        const t = setTimeout(() => {
            setBuscaDebounced(busca);
            setCurrentPage(1);
        }, 400);
        return () => clearTimeout(t);
    }, [busca]);

    //----------- CARGA INICIAL DAS TAGS + PRÉ-SELEÇÃO -----------//
    useEffect(() => {
        (async () => {
            const response = await loadTagsService("");
            if (response) {
                const tags = response.data || [];
                setTagsAvailable(tags);
                if (!preSelecionou.current) {
                    preSelecionou.current = true;
                    const idsPadrao = tags
                        .filter((tag) => {
                            const nome = normalizar(tag.nome);
                            return TERMOS_EXCLUSAO_PADRAO.some((termo) => nome.includes(termo));
                        })
                        .map((tag) => tag.id);
                    if (idsPadrao.length > 0) setExcluirTags(idsPadrao);
                }
            }
        })();
    }, []);

    //----------- CARGA DOS LEADS QUENTES -----------//
    useEffect(() => {
        let ativo = true;
        (async () => {
            setLoading(true);
            setErro(false);
            const offset = (currentPage - 1) * ITEMS_PER_PAGE;
            const comprouValor = comprou === "sim" ? true : comprou === "nao" ? false : null;
            const response = await loadLeadsQuentesService(
                ITEMS_PER_PAGE,
                offset,
                buscaDebounced,
                periodo,
                comprouValor,
                excluirTags
            );
            if (!ativo) return;
            if (response) {
                setLeads(response.data.leads || []);
                setTotal(response.data.total || 0);
            } else {
                setErro(true);
                setLeads([]);
                setTotal(0);
            }
            setLoading(false);
        })();
        return () => { ativo = false; };
    }, [currentPage, buscaDebounced, periodo, comprou, excluirTags]);

    //----------- HANDLERS -----------//
    function toggleTagExcluida(id) {
        setExcluirTags((prev) =>
            prev.includes(id) ? prev.filter((t) => t !== id) : [...prev, id]
        );
        setCurrentPage(1);
    }

    const inicio = total > 0 ? (currentPage - 1) * ITEMS_PER_PAGE + 1 : 0;
    const fim = Math.min(currentPage * ITEMS_PER_PAGE, total);


    //----------- RETORNO -----------//
    return (
        <div className="min-h-screen text-ink-1 flex relative z-0">
            <div className={`transition-all duration-300 ease-out-expo ${isOpen ? "lg:ml-64" : "sm:ml-16"} w-full max-w-full mb-16 sm:mb-0`}>
                <div className="px-5 sm:px-8 pt-8 pb-12 max-w-[1600px] mx-auto">

                    {/* HEADER */}
                    <header className="mb-8 animate-fade-in">
                        <span className="label-overline">Base</span>
                        <h1 className="display-2 flex items-center gap-3">
                            Leads Quentes
                            <Flame className="h-7 w-7 text-danger-400" strokeWidth={2.2} />
                        </h1>
                        <p className="text-ink-3 text-sm mt-1">
                            Leads com sinais de interesse recentes — carrinho abandonado, boleto/PIX gerado, tentativa de compra, resposta no WhatsApp ou visita ao site.
                        </p>
                    </header>

                    {/* BARRA DE FILTROS */}
                    <div className="surface-card p-4 mb-6 space-y-4">

                        <div className="flex flex-col lg:flex-row lg:items-end gap-4">

                            {/* PERÍODO (segmented) */}
                            <div className="flex flex-col">
                                <label className="text-xs text-ink-4 mb-1.5">Período</label>
                                <div className="inline-flex rounded-lg bg-surface-3 p-1 gap-1">
                                    {PERIODOS.map((p) => (
                                        <button
                                            key={p.valor}
                                            onClick={() => { setPeriodo(p.valor); setCurrentPage(1); }}
                                            className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                                                periodo === p.valor
                                                    ? "bg-brand-500/15 text-brand-200 ring-1 ring-brand-500/25"
                                                    : "text-ink-4 hover:text-ink-2"
                                            }`}
                                        >
                                            {p.label}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* COMPROU */}
                            <div className="flex flex-col w-full sm:max-w-[200px]">
                                <label htmlFor="filtro-comprou" className="text-xs text-ink-4 mb-1.5">Comprou</label>
                                <select
                                    id="filtro-comprou"
                                    value={comprou}
                                    onChange={(e) => { setComprou(e.target.value); setCurrentPage(1); }}
                                    className="h-[40px] bg-surface-2 border border-white/[0.12] px-4 py-2 rounded text-sm text-white focus:outline-none focus:ring-2 focus:ring-brand-500"
                                >
                                    <option value="todos">Todos</option>
                                    <option value="sim">Compraram</option>
                                    <option value="nao">Não compraram</option>
                                </select>
                            </div>

                            {/* BUSCA */}
                            <div className="flex flex-col w-full sm:max-w-[280px]">
                                <label htmlFor="filtro-busca" className="text-xs text-ink-4 mb-1.5">Buscar</label>
                                <div className="relative">
                                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-ink-4" aria-hidden="true" />
                                    <input
                                        id="filtro-busca"
                                        type="text"
                                        placeholder="Nome, email ou telefone..."
                                        value={busca}
                                        onChange={(e) => setBusca(e.target.value)}
                                        className="h-[40px] w-full pl-9 pr-4 py-2 bg-surface-2 border border-white/[0.08] rounded text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* EXCLUIR TAGS (multi-select por chips toggláveis) */}
                        <div>
                            <label className="text-xs text-ink-4 mb-1.5 block">
                                Excluir tags <span className="text-ink-5">(clique para alternar — tags de desinteresse já vêm marcadas)</span>
                            </label>
                            {tagsAvailable.length === 0 ? (
                                <span className="text-xs text-ink-5">Nenhuma tag disponível.</span>
                            ) : (
                                <div className="flex flex-wrap gap-1.5 max-h-[120px] overflow-y-auto scrollbar-none">
                                    {tagsAvailable.map((tag) => {
                                        const ativa = excluirTags.includes(tag.id);
                                        return (
                                            <button
                                                key={tag.id}
                                                onClick={() => toggleTagExcluida(tag.id)}
                                                aria-pressed={ativa}
                                                aria-label={ativa ? `Remover exclusão: ${tag.nome}` : `Excluir tag: ${tag.nome}`}
                                                className={ativa ? "chip-danger cursor-pointer" : "chip-neutral cursor-pointer hover:ring-white/[0.16]"}
                                            >
                                                {ativa && <X className="h-3 w-3" aria-hidden="true" />}
                                                {tag.nome}
                                            </button>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* CONTEÚDO */}
                    {loading ? (
                        <div className="flex items-center justify-center py-24 text-ink-3">
                            <Loader2 className="h-6 w-6 animate-spin mr-2" /> Carregando leads quentes...
                        </div>
                    ) : erro ? (
                        <div className="surface-card p-10 text-center text-danger-400">
                            Erro ao carregar leads quentes. Tente novamente.
                        </div>
                    ) : leads.length === 0 ? (
                        <div className="surface-card p-10 text-center text-ink-3">
                            Nenhum lead quente no período.
                        </div>
                    ) : (
                        <>
                            <ContainerLeadsQuentes leads={leads} />

                            {/* PAGINAÇÃO */}
                            <div className="flex justify-between items-center text-sm text-ink-3 mt-6">
                                <div>
                                    {total > 0 ? `${inicio}–${fim} de ${total}` : "Nenhum resultado"}
                                </div>
                                <div className="flex items-center gap-3">
                                    <button
                                        aria-label="Página anterior"
                                        disabled={currentPage === 1}
                                        onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                                        className="disabled:opacity-40"
                                    >
                                        <ChevronLeft className="w-4 h-4" aria-hidden="true" />
                                    </button>
                                    <span className="tabular-nums">{currentPage}</span>
                                    <button
                                        aria-label="Próxima página"
                                        disabled={currentPage * ITEMS_PER_PAGE >= total}
                                        onClick={() => setCurrentPage((p) => p + 1)}
                                        className="disabled:opacity-40"
                                    >
                                        <ChevronRight className="w-4 h-4" aria-hidden="true" />
                                    </button>
                                </div>
                            </div>
                        </>
                    )}

                </div>
            </div>
        </div>
    );
}
